import React, { useState, useRef, useEffect } from 'react';
import Seo from '@/components/Seo';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import PasswordInput from '@/components/PasswordInput';
import { useAuth } from '@/lib/auth.jsx';
import { toast } from 'sonner';
import { AlertCircle, Loader2, HelpCircle, ArrowLeft } from 'lucide-react';
import { LegalFooter } from '@/components/LegalFooter';

// Key used to remember the user's email between visits (only when "Remember me" is on).
// We never store passwords — those belong to the browser/OS password manager.
const REMEMBERED_EMAIL_KEY = 'sd_remember_email';

function lsGetSafe(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
}
function lsSetSafe(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode — ignore */ }
}
function lsRemoveSafe(key) {
    try { localStorage.removeItem(key); } catch (e) { /* private mode — ignore */ }
}

export default function Login() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();

    // If they came from a specific coach's public page, we want to send them
    // back there if they don't have an account yet. The `from` param is set by
    // CoachPublic.jsx's Log in button.
    const fromPath = params.get('from') || '';
    const fromCoachSlug = fromPath.startsWith('/coach/')
        ? fromPath.replace('/coach/', '').split('/')[0]
        : '';
    const applyHref = fromCoachSlug ? `/coach/${fromCoachSlug}/register` : '/';
    const coachLinkHref = fromCoachSlug ? `/coach/${fromCoachSlug}` : '';

    // Pre-fill from query param OR previously remembered email (mobile users hate retyping).
    const initialEmail = params.get('email') || lsGetSafe(REMEMBERED_EMAIL_KEY) || '';
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Refs used as a SAFETY NET for mobile Safari / iOS autofill:
    // when iCloud Keychain / 1Password fills the DOM, React's onChange may not fire,
    // so React state stays empty and the request gets sent with blank credentials.
    // On submit we always prefer the live DOM value over React state.
    const emailRef = useRef(null);
    const passwordRef = useRef(null);

    // After mount, sync any value the browser autofilled before React was ready.
    // Some browsers fill `<input>` values before React hydrates, leaving state in
    // a "ghost-empty" state until the user manually types something. This effect
    // pulls those autofilled values into React state so the UI reflects reality.
    useEffect(() => {
        const timer = setTimeout(() => {
            const e = emailRef.current?.value || '';
            const p = passwordRef.current?.value || '';
            if (e && !email) setEmail(e);
            if (p && !password) setPassword(p);
        }, 150);
        return () => clearTimeout(timer);
        // run once on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError('');

        // CRITICAL: read straight from the DOM as a fallback for autofilled values
        // that never made it into React state. This is the #1 fix for the iOS
        // "wrong email and password" report.
        const liveEmail = (emailRef.current?.value || email || '').trim();
        const livePassword = passwordRef.current?.value || password || '';

        if (!liveEmail || !livePassword) {
            const msg = !liveEmail && !livePassword
                ? 'Enter your email and password.'
                : !liveEmail ? 'Enter your email.' : 'Enter your password.';
            setError(msg);
            toast.error(msg);
            return;
        }

        setLoading(true);
        try {
            const result = await login(liveEmail, livePassword, remember);

            // If the account has 2FA enabled and we don't have a valid
            // trusted-device cookie, the server returns a short-lived
            // mfa_token + the available methods. We hop to the 2FA verify
            // page carrying that challenge in router state (NOT in the URL
            // — it's a one-time secret).
            if (result?.challenge) {
                if (remember) lsSetSafe(REMEMBERED_EMAIL_KEY, liveEmail);
                else lsRemoveSafe(REMEMBERED_EMAIL_KEY);
                const nextParam = params.get('next') || '';
                navigate('/login/2fa', {
                    replace: true,
                    state: {
                        mfa_token: result.mfa_token,
                        methods: result.methods,
                        email_masked: result.email_masked,
                        remember_me: result.remember_me,
                        next: nextParam,
                    },
                });
                return;
            }

            const u = result;

            // Persist email locally so next visit on this device pre-fills it.
            // We never store the password — that belongs to the OS password manager.
            if (remember) lsSetSafe(REMEMBERED_EMAIL_KEY, liveEmail);
            else lsRemoveSafe(REMEMBERED_EMAIL_KEY);

            const next = params.get('next');
            if (next) navigate(next, { replace: true });
            else if (u.role === 'instructor') {
                if (u.onboarding_completed === false || (u.instructor_profile && u.instructor_profile.onboarding_completed === false)) {
                    navigate('/instructor/setup-quiz', { replace: true });
                } else navigate('/instructor/dashboard', { replace: true });
            }
            else if (u.role === 'parent') navigate('/parent/dashboard', { replace: true });
            else if (u.role === 'staff_coach') navigate('/staff/dashboard', { replace: true });
            else navigate('/admin/dashboard', { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail
                || (err?.message?.includes('Network') ? "Couldn't reach SplashPad — check your connection and try again." : null)
                || err?.message
                || 'Login failed. Please try again.';
            setError(msg);
            toast.error(msg, { duration: 6000 });
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-dvh bg-[#f4f7fb] flex flex-col">
            <Seo
                title="Log in to SplashPad"
                description="Log in to your SplashPad coach, parent, or staff account to manage swim lessons, schedules, waivers, and payments."
                canonical="https://splashpad.pro/login"
                noIndex
            />
            <div className="px-4 py-4 sm:px-6">
                <Link to="/" data-testid="login-back-home-link"><Logo /></Link>
            </div>
            <div className="flex flex-1 items-center justify-center px-4 pb-32 sm:pb-12 sm:px-6">
                <Card className="sd-card w-full max-w-md p-6 sm:p-8">
                    <h1 className="font-display text-3xl font-bold text-[#0a1628]" data-testid="login-welcome-heading">
                        Welcome back! 👋
                    </h1>
                    <p className="mt-2 text-sm text-[#1a3a5c]">
                        Log back into your account to check on your lessons, manage your students, or pick up where you left off.
                    </p>

                    {/* "Are you in the right place?" signpost — coaches got feedback that
                        first-time parents were clicking Log in by mistake. This card makes
                        the distinction obvious before they start typing the wrong email. */}
                    <div
                        className="mt-4 flex items-start gap-2 rounded-xl border border-[#00d4ff]/40 bg-[#e6fbff] px-3 py-2.5 text-xs text-[#0a1628]"
                        data-testid="login-new-here-signpost"
                    >
                        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#1a3a5c]" />
                        <span>
                            <strong>New to SplashPad?</strong>{' '}
                            {fromCoachSlug ? (
                                <>You need to apply through your coach first.{' '}
                                    <Link to={applyHref} className="font-semibold underline hover:text-[#1a3a5c]" data-testid="login-apply-here-link">
                                        Apply with your coach →
                                    </Link>
                                </>
                            ) : (
                                <>You'll need to apply through your coach's page first — they'll send you a welcome email once you're approved.</>
                            )}
                        </span>
                    </div>

                    {/* `action` + `method` are no-op fallbacks for password managers that key
                        off the form action URL when deciding to offer save/autofill. */}
                    <form
                        id="login-form"
                        name="login"
                        action="/login"
                        method="post"
                        onSubmit={submit}
                        className="mt-6 space-y-4"
                        data-testid="login-form"
                        noValidate
                    >
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                ref={emailRef}
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 h-11 rounded-xl"
                                autoComplete="username"
                                inputMode="email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="next"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="pw">Password</Label>
                                <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`} className="text-xs font-semibold text-[#1a3a5c] hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
                            </div>
                            <PasswordInput
                                ref={passwordRef}
                                id="pw"
                                name="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 h-11 rounded-xl"
                                autoComplete="current-password"
                                enterKeyHint="go"
                                data-testid="login-password-input"
                                testIdShow="login-password-toggle"
                            />
                        </div>
                        <label htmlFor="remember" className="flex items-start gap-2 select-none cursor-pointer">
                            <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="mt-0.5" data-testid="login-remember-checkbox" />
                            <span className="text-sm text-[#1a3a5c]">Remember this browser for 30 days</span>
                        </label>

                        {/* Inline error — always visible even when Sonner toast is occluded on iOS Safari */}
                        {error && (
                            <div
                                role="alert"
                                aria-live="assertive"
                                className="flex items-start gap-2 rounded-xl border border-[#ff6b35]/40 bg-[#ff6b35]/10 px-3 py-2.5 text-sm text-[#0a1628]"
                                data-testid="login-error-message"
                            >
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6b35]" />
                                <span>{error}</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            className="sd-cta sd-focus w-full h-11 rounded-xl"
                            data-testid="login-submit-button"
                        >
                            {loading ? (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Logging in…</>) : 'Log in'}
                        </Button>
                    </form>
                    <div className="mt-4 text-sm text-[#1a3a5c]">
                        New instructor? <Link to="/signup" className="font-semibold text-[#0a1628] hover:underline" data-testid="login-signup-link">Create an account</Link>
                    </div>
                    {coachLinkHref && (
                        <div className="mt-2 border-t pt-3 text-xs text-[#1a3a5c]/70">
                            <Link
                                to={coachLinkHref}
                                className="inline-flex items-center gap-1 hover:text-[#0a1628] hover:underline"
                                data-testid="login-back-to-coach-link"
                            >
                                <ArrowLeft className="h-3 w-3" /> Back to your coach's page
                            </Link>
                        </div>
                    )}
                </Card>
            </div>
            <LegalFooter />
        </div>
    );
}
